import { FC, useCallback, useMemo, useState } from 'react';
import { BrokerDto, DispatchReviewDto, DriverDto, EquipmentDto, FreightBillingLineItemDto, OrderByType, OrderDto, OrderReviewLineItemDto } from '../../../dtos';
import _ from 'lodash';
import { AssignmentType } from '../../../dtos/generated/AssignmentType';
import { Box, FormControl, FormHelperText, FormLabel, Grid, IconButton, MenuItem, Select, SelectChangeEvent, Tab, Tooltip, Typography } from '@mui/material';
import { IEntityAutocomplete } from '../../CommonInputs/IEntityAutocomplete';
import { FormInput, FormNumberInput } from '../../CoreLib/library';
import { AddCircle, Error } from '@mui/icons-material';
import { SelectableList, TimePicker, EquipmentSelect } from '../../CommonInputs';
import { formatTime, LineItemTypes } from '../../../util';
import { FreightBillingLineItemTable } from './FreightBillingLineItemTable';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { isErrorInMap } from './useOrderReviewForm';
import { getUniqueKey } from '../../../util/getUniqueKey';
import { adjustSecondDateToMatchFirst } from '../../../util/adjustDates';

export interface IDispatchReviewTableProps {
    brokers: BrokerDto[];
    disabled: boolean;
    dispatches: DispatchReviewDto[];
    drivers: DriverDto[];
    orderReviewLineItem: OrderReviewLineItemDto;
    order: OrderDto;
    removeDispatchReview: (dispatchIndex: number) => void;
    setDispatches: (dispatches: React.SetStateAction<DispatchReviewDto[]>) => void;
    addFreightBillingLineItem: (dispatchIndex: number) => void;
    removeFreightBillingLineItem: (dispatchIndex: number, freightLineItemIndex: number) => void;
    addDispatchReview: () => void;
    dispatchErrors: Map<number, Map<keyof DispatchReviewDto, string>>;
    freightErrors: Map<number, Map<number, Map<keyof FreightBillingLineItemDto, string>>>;
    setOrderReviewLineItem: (lineItem: OrderReviewLineItemDto) => void;
    handleUpdatedFreightBillingNumber: (updatedFreightBillingNumber: string, orderLineItemIndex: number, dispatchIndex: number, freightBillingLineItemIndex: number) => void;
}

export const DispatchReviewTable: FC<IDispatchReviewTableProps> = ({
    order,
    brokers,
    disabled,
    dispatches,
    drivers,
    orderReviewLineItem,
    removeDispatchReview,
    setDispatches,
    removeFreightBillingLineItem,
    addFreightBillingLineItem,
    addDispatchReview,
    dispatchErrors,
    freightErrors,
    setOrderReviewLineItem,
    handleUpdatedFreightBillingNumber,
}) => {

    const [selectedTab, setSelectedTab] = useState('0');
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

    const calculateHourlyHaulQuantity = useCallback((data: DispatchReviewDto) => {
        if (!data.startDate || !data.endDate) return 0;
        const hours = (new Date(data.endDate).getTime() - new Date(data.startDate).getTime()) / (1000 * 60 * 60);
        return hours >= 0 ? hours : 0;
    }, []);

    const updateDispatch = useCallback(
        (index: number, fieldName: string, value: any) => {
            var updatedDispatches = _.cloneDeep(dispatches);
            var updatedDispatchReview = { ...updatedDispatches[index] };

            updatedDispatchReview[fieldName] = value;

            if (fieldName === 'assignmentType') {
                if (updatedDispatchReview.assignmentType === AssignmentType.Broker) {
                    updatedDispatchReview.driverId = undefined;
                    updatedDispatchReview.driver = undefined;
                    updatedDispatchReview.equipmentId = undefined;
                    updatedDispatchReview.equipment = undefined;
                } else {
                    updatedDispatchReview.brokerId = undefined;
                    updatedDispatchReview.broker = undefined;
                    updatedDispatchReview.equipmentId = undefined;
                    updatedDispatchReview.equipment = undefined;
                }
            }

            if (fieldName === 'startDate' || fieldName === 'endDate') {
                if (orderReviewLineItem?.lineItem && orderReviewLineItem.lineItem.type === LineItemTypes.Hourly) {
                    updatedDispatchReview.haulQuantity = calculateHourlyHaulQuantity(updatedDispatchReview);
                }
            }

            updatedDispatches.splice(index, 1, updatedDispatchReview);

            setDispatches(updatedDispatches);
        },
        [dispatches, setDispatches, orderReviewLineItem.lineItem, calculateHourlyHaulQuantity]
    );

    const handleBrokerChange = useCallback(
        (index: number, broker?: BrokerDto) => {
            var updatedDispatches = _.cloneDeep(dispatches);
            var updatedDispatchReview = { ...updatedDispatches[index] };

            updatedDispatchReview['broker'] = broker;
            updatedDispatchReview['brokerId'] = broker?.id;
            updatedDispatches.splice(index, 1, updatedDispatchReview);

            updatedDispatchReview['equipment'] = undefined;
            updatedDispatchReview['equipmentId'] = undefined;
            updatedDispatches.splice(index, 1, updatedDispatchReview);

            setDispatches(updatedDispatches);

        },
        [dispatches, setDispatches]
    );

    const handleDriverChange = useCallback(
        (index: number, driver?: DriverDto) => {
            var updatedDispatches = _.cloneDeep(dispatches);
            var updatedDispatchReview = { ...updatedDispatches[index] };

            updatedDispatchReview['driver'] = driver;
            updatedDispatchReview['driverId'] = driver?.id;
            updatedDispatchReview['equipmentId'] = driver?.equipmentId;
            updatedDispatchReview['equipment'] = driver?.equipment;
            updatedDispatches.splice(index, 1, updatedDispatchReview);

            setDispatches(updatedDispatches);
        },
        [dispatches, setDispatches]
    );

    const handleEquipmentChange = useCallback(
        (index: number, equipment?: EquipmentDto) => {
            var updatedDispatches = _.cloneDeep(dispatches);
            var updatedDispatchReview = { ...updatedDispatches[index] };

            updatedDispatchReview['equipment'] = equipment;
            updatedDispatchReview['equipmentId'] = equipment?.id;
            updatedDispatches.splice(index, 1, updatedDispatchReview);

            setDispatches(updatedDispatches);
        },
        [dispatches, setDispatches]
    );

    const checkDispatchForGeneralErrors = useCallback(
        (index: number) => {
            const errorMap = dispatchErrors.get(index);
            const generalFields: (keyof DispatchReviewDto)[] = ['brokerId', 'driverId', 'equipmentId', 'onsiteTime', 'startDate', 'endDate', 'driverComment'];
            return generalFields.some((x) => !!errorMap?.get(x));
        },
        [dispatchErrors]
    );

    const checkDispatchForHaulToFromErrors = useCallback(
        (index: number) => {
            const errorMap = dispatchErrors.get(index);
            const haulToFromFields: (keyof DispatchReviewDto)[] = [
                'haulToAddressLine1',
                'haulToAddressLine2',
                'haulToCity',
                'haulToState',
                'haulToZipCode',
                'haulToCountry',
                'haulToDirections',
                'haulFromAddressLine1',
                'haulFromAddressLine2',
                'haulFromCity',
                'haulFromState',
                'haulFromZipCode',
                'haulFromCountry',
                'haulFromDirections',
            ];
            return haulToFromFields.some((x) => !!errorMap?.get(x));
        },
        [dispatchErrors]
    );

    const checkDispatchForBillingErrors = useCallback(
        (index: number) => {
            const errorMaps = freightErrors.get(index);
            return Array.from(errorMaps?.values() ?? []).some((billingErrorMap) => isErrorInMap(billingErrorMap));
        },
        [freightErrors]
    );

    const checkDispatchForErrors = useCallback(
        (index: number) => {
            const isGeneralErrors = checkDispatchForGeneralErrors(index);
            const isHaulToFromErrors = checkDispatchForHaulToFromErrors(index);
            const isBillingErrors = checkDispatchForBillingErrors(index);

            return isGeneralErrors || isHaulToFromErrors || isBillingErrors;
        },
        [checkDispatchForGeneralErrors, checkDispatchForHaulToFromErrors, checkDispatchForBillingErrors]
    );

    const buildBrokerString = useCallback((broker: BrokerDto | undefined) => {
        if (!!broker) {
            return `${broker.code} - ${broker.name}`;
        } else {
            return '';
        }
    }, []);

    const buildDriverString = useCallback((driver: DriverDto | undefined) => {
        if (!!driver) {
            return `${driver.code} - ${driver.name}`;
        } else {
            return '';
        }
    }, []);

    const hasHaulQuantity = useMemo(() => {
        return orderReviewLineItem.orderBy !== OrderByType.Equipment && !(orderReviewLineItem.lineItem?.type === LineItemTypes.Hourly && orderReviewLineItem.orderBy === OrderByType.Quantity);
    }, [orderReviewLineItem]);

    const generalDetails = useCallback(
        (dispatchReview: DispatchReviewDto, index: number) => {
            var errors = dispatchErrors.get(index);

            return (
                <Grid item container direction='column' key={getUniqueKey(dispatchReview)}>
                    <Grid item container direction='row' xs={12} spacing={1}>
                        <Grid item xs={12} xl={!hasHaulQuantity ? 4 : 3}>
                            <FormControl fullWidth disabled={disabled} error={!!errors?.get('assignmentType')}>
                                <FormLabel>Assign. Type</FormLabel>
                                <Select
                                    value={dispatchReview.assignmentType === AssignmentType.Broker ? 'Broker' : 'Driver'}
                                    onChange={(event: SelectChangeEvent) => {
                                        const value = event.target.value === 'Broker' ? AssignmentType.Broker : AssignmentType.Driver;
                                        updateDispatch(index, 'assignmentType', value);
                                    }}>
                                    <MenuItem value={'Broker'}>Broker</MenuItem>
                                    <MenuItem value={'Driver'}>Driver</MenuItem>
                                </Select>
                                {errors?.get('assignmentType') && <FormHelperText>{errors?.get('assignmentType')}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        {dispatchReview.assignmentType === AssignmentType.Driver && (
                            <Grid item xs={12} xl={!hasHaulQuantity ? 4 : 3} spacing={1}>
                                <FormControl error={!!errors?.get('driverId')} fullWidth required disabled={disabled}>
                                    <FormLabel>Driver</FormLabel>
                                    <IEntityAutocomplete
                                        options={drivers}
                                        onChange={(e, value) => {
                                            handleDriverChange(index, value ?? null);
                                        }}
                                        value={dispatchReview.driver}
                                        getOptionLabel={(option: DriverDto) => buildDriverString(option)}
                                        error={!!errors?.get('driverId')}
                                        disabled={disabled}
                                    />
                                    {errors?.get('driverId') && <FormHelperText>{errors?.get('driverId')}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        )}
                        {dispatchReview.assignmentType === AssignmentType.Broker && (
                            <Grid item xs={12} xl={!hasHaulQuantity ? 4 : 3}>
                                <FormControl error={!!errors?.get('brokerId')} fullWidth required disabled={disabled}>
                                    <FormLabel>Broker</FormLabel>
                                    <IEntityAutocomplete
                                        options={brokers}
                                        onChange={(e, value) => {
                                            handleBrokerChange(index, value ?? null);
                                        }}
                                        value={dispatchReview.broker}
                                        getOptionLabel={(option: BrokerDto) => `${option.code} - ${option.name}`}
                                        error={!!errors?.get('brokerId')}
                                        disabled={disabled}
                                    />
                                    {errors?.get('brokerId') && <FormHelperText>{errors?.get('brokerId')}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={12} xl={!hasHaulQuantity ? 4 : 3}>
                            <FormControl
                                error={!!errors?.get('equipmentId')}
                                fullWidth
                                required={dispatchReview.assignmentType === AssignmentType.Driver}
                                disabled={disabled}>
                                <EquipmentSelect
                                    selectedEquipmentId={dispatchReview.equipmentId ?? ''}
                                    handleSelectedEquipmentChange={(e) => handleEquipmentChange(index, e ?? undefined)}
                                    brokerId={selectedIndex !== undefined ? dispatches[selectedIndex]?.brokerId : undefined}
                                    disabled={disabled}
                                />
                                {errors?.get('equipmentId') && <FormHelperText>{errors?.get('equipmentId')}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        {hasHaulQuantity &&
                            <Grid item xs={12} xl={3}>
                                <FormControl fullWidth disabled={disabled} error={!!errors?.get('haulQuantity')}>
                                    <FormNumberInput
                                        label='Haul Quantity'
                                        name='haulQuantity'
                                        value={dispatchReview.haulQuantity}
                                        onChange={(event) => updateDispatch(index, 'haulQuantity', Number(event.target.value))}
                                        disabled={disabled}
                                    />
                                    {errors?.get('haulQuantity') && <FormHelperText>{errors?.get('haulQuantity')}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        }
                        <Grid item xs={12} xl={4}>
                            <TimePicker
                                value={dispatchReview.yardTime}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(order.orderDate, date);
                                        updateDispatch(index, 'yardTime', adjustedDate);
                                    }
                                }}
                                label='Yard Time'
                                disabled={disabled}
                                error={!!errors?.get('yardTime')}
                                errorText={errors?.get('yardTime')}
                            />
                        </Grid>
                        <Grid item xs={12} xl={4}>
                            <TimePicker
                                value={dispatchReview.startDate}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(order.orderDate, date);
                                        updateDispatch(index, 'startDate', adjustedDate);
                                    }
                                }}
                                error={!!errors?.get('startDate')}
                                errorText={errors?.get('startDate')}
                                label='Onsite Time'
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={12} xl={4}>
                            <TimePicker
                                value={dispatchReview.endDate}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(order.orderDate, date);
                                        updateDispatch(index, 'endDate', adjustedDate);
                                    }
                                }}
                                error={!!errors?.get('endDate')}
                                errorText={errors?.get('endDate')}
                                label='End Time'
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <FormInput
                            label='Driver Comment'
                            fullWidth
                            value={dispatchReview.driverComment}
                            onChange={
                                (e) => updateDispatch(index, 'driverComment', e.target.value)
                            }
                            rows={2}
                            disabled={disabled}
                        />
                    </Grid>
                </Grid>
            );
        },
        [
            brokers,
            order.orderDate,
            buildDriverString,
            disabled,
            drivers,
            handleBrokerChange,
            handleDriverChange,
            handleEquipmentChange,
            updateDispatch,
            dispatchErrors,
            dispatches,
            selectedIndex,
            hasHaulQuantity
        ]
    );

    const billingDetails = useCallback(
        (dispatchReview: DispatchReviewDto, dispatchReviewIndex: number) => {
            return (
                <Grid item container direction='column' height='100%' wrap='nowrap'>
                    {(dispatchReview.freightBillingLineItems ?? []).length === 0 && (
                        <Grid item textAlign='center' xs={12}>
                            <Typography color='GrayText'>No billing items have been added to this dispatch.</Typography>
                        </Grid>
                    )}
                    {(dispatchReview.freightBillingLineItems ?? []).length > 0 && (
                        <Grid item xs={12}>
                            <FreightBillingLineItemTable
                                orderReviewLineItem={orderReviewLineItem}
                                order={order}
                                dispatchReview={dispatchReview}
                                removeFreightBillingLineItem={(freightLineItemIndex) => removeFreightBillingLineItem(dispatchReviewIndex, freightLineItemIndex)}
                                updateFreightBillingLineItemField={(
                                    frightLineItemIndex,
                                    key,
                                    value
                                ) => {
                                    if (key === 'freightBillNumber') {
                                        handleUpdatedFreightBillingNumber(value as string, orderReviewLineItem.orderLineItemNumber - 1, dispatchReviewIndex, frightLineItemIndex);
                                        // The second argument is reduced by 1 since we want the variable index, not the user-visible index.
                                    }

                                    setDispatches((prev: DispatchReviewDto[]) => {
                                        return prev.map((dispatch, i) => {
                                            if (i === dispatchReviewIndex) {
                                                return {
                                                    ...dispatch,
                                                    freightBillingLineItems: dispatch.freightBillingLineItems?.map((lineItem, j) => {
                                                        if (j === frightLineItemIndex) {
                                                            return {
                                                                ...lineItem,
                                                                [key]: value
                                                            };
                                                        }
                                                        return lineItem;
                                                    })
                                                };
                                            }
                                            return dispatch;
                                        });
                                    });
                                }}
                                updateFreightBillingLineItem={(freightLineItemIndex, freightLineItem) => {
                                    setDispatches((prev: DispatchReviewDto[]) => {
                                        return prev.map((dispatch, i) => {
                                            if (i === dispatchReviewIndex) {
                                                return {
                                                    ...dispatch,
                                                    freightBillingLineItems: dispatch.freightBillingLineItems?.map((lineItem, j) => {
                                                        if (j === freightLineItemIndex) {
                                                            return freightLineItem;
                                                        }
                                                        return lineItem;
                                                    })
                                                };
                                            }
                                            return dispatch;
                                        });
                                    });
                                }}
                                freightErrors={freightErrors.get(dispatchReviewIndex) ?? new Map<number, Map<keyof FreightBillingLineItemDto, string>>()}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Tooltip title='Add Freight Billing'>
                            <IconButton
                                size='small'
                                color='primary'
                                sx={{ marginTop: '12px' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    addFreightBillingLineItem(dispatchReviewIndex);
                                }}>
                                <AddCircle />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            );
        },
        [
            addFreightBillingLineItem,
            order,
            orderReviewLineItem,
            removeFreightBillingLineItem,
            setDispatches,
            freightErrors,
            handleUpdatedFreightBillingNumber
        ]
    );

    const details = useCallback(
        (item?: DispatchReviewDto, index?: number) => {
            const isGeneralErrors = index !== undefined ? checkDispatchForGeneralErrors(index) : false;
            const isHaulToFromErrors = index !== undefined ? checkDispatchForHaulToFromErrors(index) : false;
            const isBillingErrors = index !== undefined ? checkDispatchForBillingErrors(index) : false;

            return (
                <Box key={item?.id} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                    <TabContext value={item ? selectedTab : '0'}>
                        <Box sx={{ width: '100%', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
                            <TabList variant='fullWidth' sx={{ width: '100%' }} onChange={(_e, newValue) => setSelectedTab(newValue)}>
                                <Tab
                                    label='General'
                                    value='0'
                                    iconPosition='end'
                                    icon={(isGeneralErrors || isHaulToFromErrors) ? <Error color='error' /> : undefined}
                                    sx={{ minHeight: '48px' }}
                                />
                                <Tab
                                    label='Billing'
                                    value='1'
                                    iconPosition='end'
                                    icon={isBillingErrors ? <Error color='error' /> : undefined}
                                    sx={{ minHeight: '48px' }}
                                />
                            </TabList>
                        </Box>
                        {!item && (
                            <Box textAlign='center'>
                                <Typography mt={2} color='GrayText'>
                                    No Item Selected
                                </Typography>
                            </Box>
                        )}
                        <TabPanel value='0' sx={{ overflowY: 'auto' }}>
                            {item && index !== undefined && (
                                <>
                                    {generalDetails(item, index)}
                                    <Box mt={2} />
                                </>
                            )}
                        </TabPanel>
                        <TabPanel value='1' sx={{ overflowY: 'auto' }}>
                            {item && index !== undefined && billingDetails(item, index)}
                        </TabPanel>
                    </TabContext>
                </Box>
            );
        },
        [
            billingDetails,
            generalDetails,
            selectedTab,
            checkDispatchForGeneralErrors,
            checkDispatchForHaulToFromErrors,
            checkDispatchForBillingErrors,
        ]
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', flexGrow: 1, height: '100%' }}>
            <SelectableList
                items={dispatches}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                hasError={checkDispatchForErrors}
                getKey={(item, index) => (item as DispatchReviewDto & { clientTempId?: string }).clientTempId ?? item.id ?? `dispatch-${index}`}
                getDesc={(item, index) =>
                    `${index + 1} [${item.startDate ? formatTime(item.startDate) : '???'} - ${item.endDate ? formatTime(item.endDate) : '???'}] - ${item.assignmentType === AssignmentType.Broker ? buildBrokerString(item.broker) : buildDriverString(item.driver)
                    }`
                }
                style={{ borderRight: 1, borderColor: 'divider', minWidth: 240, height: '100%', bgcolor: 'background.paper', overflowY: 'auto' }}
                addItem={addDispatchReview}
                disableAdd={disabled}
                removeItem={(_, i) => {
                    removeDispatchReview(i);
                    setSelectedIndex(undefined);
                }}
                disableRemove={(item: DispatchReviewDto) => disabled || (item.freightBillingLineItems?.filter((x) => !!x.invoiceId).length ?? 0) > 0}
            />
            {details(selectedIndex !== undefined ? dispatches[selectedIndex] : undefined, selectedIndex)}
        </Box>
    );
};
